import axios from "axios";
import { Email } from "@/api/email";

export enum MailboxType {
  POP3 = "POP3",
  IMAP = "IMAP",
  OFFICE_365 = "OFFICE_365",
}

export interface MailboxFolder {
  id: string;
  name: string;
  subFolders: MailboxFolder[];
}

export interface Mailbox {
  id: string | null;
  name: string;
  description: string | null;
  username: string | null;
  password: string | null;
  serverUrl: string | null;
  serverPort: string | null;
  tenantId: string | null;
  applicationId: string | null;
  alternativeMailboxForContactImport: string | null;
  type: MailboxType;
  suitableMailFolders: string[] | null;
}

export interface JobInfo {
  id: string | null;
  name: string;
  message: string | null;
  startedUtc: Date | null;
  finishedUtc: Date | null;
}

interface MailboxApi {
  list(): Promise<Mailbox[]>;
  add(mailbox: Mailbox): Promise<string>;
  edit(id: string | null, mailbox: Mailbox): Promise<void>;
  delete(id: string | null): Promise<void>;
  listJobs(): Promise<JobInfo[]>;
  startImportingContacts(): Promise<void>;
  startSynchronizeContactHistory(from: string, to: string): Promise<void>;
  listMailboxFolder(mailboxId: string | null): Promise<MailboxFolder>;
  sendMessage(
    mailboxId: string,
    rootEmailId: string | null,
    message: Email,
    files: File[],
    contactIdForFurtherEncounter: string | null
  ): Promise<void>;
}

export const mailboxApi: MailboxApi = {
  async list() {
    return (await axios.get("/api/mailboxes")).data;
  },

  async edit(id, mailbox) {
    return await axios.put(`/api/mailboxes/${id}`, mailbox);
  },

  async add(mailbox) {
    return (await axios.post("/api/mailboxes", mailbox)).data;
  },

  async delete(id) {
    return await axios.delete(`/api/mailboxes/${id}`);
  },

  async listJobs() {
    const result = (await axios.get(`/api/mailboxes/import`)).data;

    if (result) {
      return result.map(
        (v: JobInfo) =>
          ({
            ...v,
            startedUtc: v.startedUtc ? new Date(v.startedUtc) : null,
            finishedUtc: v.finishedUtc ? new Date(v.finishedUtc) : null,
          } as JobInfo)
      );
    }
    return [];
  },

  async startImportingContacts() {
    return await axios.post("/api/mailboxes/import/_contact");
  },

  async startSynchronizeContactHistory(from, to) {
    return await axios.post(
      `/api/mailboxes/synchronize/_contactHistory?from=${from}&to=${to}`
    );
  },

  async listMailboxFolder(id) {
    return (await axios.get(`/api/mailboxes/${id}/folders`)).data;
  },

  async sendMessage(
    mailboxId,
    rootEmailId,
    message,
    files,
    contactIdForFurtherEncounter
  ) {
    const formData = new FormData();

    formData.append("message", JSON.stringify(message));
    if (files && files.length < 50) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        formData.append("files", file);
      }
    }
    return await axios.post(
      `/api/mailboxes/${mailboxId}/_sendMessage`,
      formData,
      {
        params: {
          rootEmailId,
          contactIdForFurtherEncounter,
        },
      }
    );
  },
};
